import { useContext, useRef } from "react";
import { CompanyContext } from "@/components/CompanyList/CompanyContext";
import * as constants from "@/config/constants";
import { getIsNotUserFiltered } from "@/selectors";
import { useSelector } from "react-redux";
import { Transition } from "react-transition-group";

import { ServiceFlagTrophyIcon } from "../../../../besteducation/assets/ServiceFlagTrophyIcon";
import { CheckIcon } from "../../../../client/assets/icons/common/CheckIcon";
import { RightArrowIcon } from "../../../../client/assets/icons/common/RightArrowIcon";
import { BaseBenefitsList } from "../../../../components/BaseComponents/BaseBenefitsList";
import { BaseCompanyLogo } from "../../../../components/BaseComponents/BaseCompanyLogo";
import BaseCouponDescription from "../../../../components/BaseComponents/BaseCouponDescription";
import { BaseFeaturedCompanyHeader } from "../../../../components/BaseComponents/BaseFeaturedCompanyHeader";
import BaseGridRatingHorizontal from "../../../../components/BaseComponents/BaseGridRatingHorizontal";
import BaseLinkWrapper from "../../../../components/BaseComponents/BaseLinkWrapper";
import BaseProductDescription from "../../../../components/BaseComponents/BaseProductDescription";
import BaseServiceFlag from "../../../../components/BaseComponents/BaseServiceFlag";
import { BaseSocialProof } from "../../../../components/BaseComponents/BaseSocialProof";
import { BaseVisitLink } from "../../../../components/BaseComponents/BaseVisitLink";
import { MedicationDetails } from "./MedicationDetails";
import { getStyles } from "./styles";

const COLUMN = "tw:contents tw:lg:flex tw:lg:flex-col";

export const WeightLossCompany = () => {
	const { company, companyIndex, isFeaturedCompany } =
		useContext(CompanyContext);
	const { isNotUserFiltered } = useSelector(getIsNotUserFiltered);
	const styles = getStyles({ companyIndex });
	const nodeRef = useRef(null);
	const { actives, couponDescription, formats } = company?.fields || {};
	const hasSocialProof = companyIndex === 0;
	const hasMedicationDetails = Boolean(formats || actives);
	const hasCoupon = Boolean(couponDescription);

	return (
		<Transition
			nodeRef={nodeRef}
			in
			appear={!isNotUserFiltered}
			timeout={constants.FILTERING_TRANSITION_DURATION}
			unmountOnExit
		>
			<BaseLinkWrapper
				className={`tw:mb-6 tw:block tw:rounded-lg tw:bg-white tw:shadow-[0px_4px_24px_0px_rgba(88,102,126,0.06),0px_1px_2px_0px_rgba(88,102,126,0.12)] tw:transition tw:hover:shadow-[0px_8px_32px_0px_rgba(0,0,0,0.08),0px_2px_20px_0px_rgba(0,0,0,0.04)] ${
					isFeaturedCompany ? "tw:ring-1 tw:ring-[#359492]" : ""
				}`}
			>
				<BaseFeaturedCompanyHeader {...styles.featuredCompanyHeader} />

				<div className="tw:relative">
					<BaseServiceFlag
						classNames={styles.serviceFlag}
						icon={
							<ServiceFlagTrophyIcon className="tw:h-3 tw:w-3.5 tw:lg:size-4" />
						}
					/>

					<div
						className={`tw:mx-auto tw:grid tw:max-w-[400px] tw:grid-cols-[auto_1fr] tw:gap-x-4 tw:p-4 tw:pt-16 tw:lg:flex tw:lg:max-w-none tw:lg:flex-row tw:lg:gap-0 tw:lg:pt-4 tw:lg:pb-4 tw:lg:pl-20 tw:xl:py-6 tw:xl:pr-6 tw:xl:pl-24 ${
							hasSocialProof ? "tw:pb-[52px]" : ""
						}`}
					>
						<div
							className={`${COLUMN} tw:lg:w-[232px] tw:lg:shrink-0 tw:lg:items-center tw:lg:justify-center tw:lg:gap-10`}
						>
							<div className="tw:order-1 tw:col-span-2 tw:flex tw:justify-center tw:lg:order-0">
								<BaseCompanyLogo classNames={styles.companyLogo} />
							</div>
							<div className="tw:order-4 tw:col-span-2 tw:mt-5 tw:self-center tw:justify-self-center tw:xxs:col-span-1 tw:xxs:justify-self-auto tw:lg:order-0 tw:lg:mt-0">
								<BaseGridRatingHorizontal classNames={styles.rating} />
							</div>
						</div>

						<div
							className={`${COLUMN} tw:lg:grow tw:lg:justify-center tw:lg:pr-6 tw:lg:pl-6 tw:xl:pr-12 tw:xl:pl-12`}
						>
							<div className="tw:order-2 tw:col-span-2 tw:mt-6 tw:lg:order-0 tw:lg:mt-0">
								<BaseProductDescription className={styles.productDescription} />
							</div>
							{hasMedicationDetails ? (
								<div className="tw:order-6 tw:col-span-2 tw:mt-4 tw:lg:order-0">
									<MedicationDetails classNames={styles.medicationDetails} />
								</div>
							) : null}
							<div
								className={`tw:order-7 tw:col-span-2 tw:lg:order-0 ${hasMedicationDetails ? "tw:mt-2" : "tw:mt-4"}`}
							>
								<BaseBenefitsList
									{...styles.benefitsList}
									icon={<CheckIcon className="tw:text-base tw:sm:mt-1" />}
								/>
							</div>
						</div>

						<div
							className={`${COLUMN} tw:lg:w-[178px] tw:lg:shrink-0 tw:lg:items-center tw:lg:justify-center`}
						>
							{hasSocialProof ? (
								<div className="tw:absolute tw:inset-x-0 tw:bottom-0 tw:lg:static tw:lg:order-0">
									<BaseSocialProof
										{...styles.socialProof}
										contentText="81% of our visitors chose this"
									/>
								</div>
							) : null}
							<div className="tw:order-5 tw:col-span-2 tw:mt-5 tw:xxs:col-span-1 tw:lg:order-0 tw:lg:mt-0 tw:lg:w-full">
								<BaseVisitLink {...styles.visitLink} icon={<RightArrowIcon />}>
									Visit Website
								</BaseVisitLink>
							</div>
							{hasCoupon ? (
								<div className="tw:order-3 tw:col-span-2 tw:mt-4 tw:lg:order-0 tw:lg:mt-6 tw:lg:w-full">
									<BaseCouponDescription
										classNames={styles.couponDescription}
									/>
								</div>
							) : null}
						</div>
					</div>
				</div>
			</BaseLinkWrapper>
		</Transition>
	);
};
